import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { Month, UserType } from "common";
import { AgingReportWithStatusesByMonth } from "../util/getAgingReportStatus";

export const PROFILE_STORAGE_KEY = "authProfile";
// Key used before the move to Cognito (it held a hand-made token); such sessions are not valid any more
const LEGACY_SESSION_STORAGE_KEY = "userState";

export const initialUserState: any = {
  username: "",
  email: "",
  group: "",
  type: UserType.Regular,
  facilities: [],
  facilitiesPayers: {},
};

const loadProfile = () => {
  localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  try {
    return JSON.parse(localStorage.getItem(PROFILE_STORAGE_KEY) || "null") ?? initialUserState;
  } catch {
    return initialUserState;
  }
};

const profileAtom = atom<any>(loadProfile());

/**
 * The signed-in user's profile (not the tokens: the auth library owns those). Read synchronously from storage so
 * a returning user sees the app immediately; the session behind it is verified in the background.
 */
export const userState = atom(
  (get) => get(profileAtom),
  (_get, set, next: any) => {
    set(profileAtom, next);
    if (next?.username) localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(PROFILE_STORAGE_KEY);
  }
);

export interface ReportsState {
  reports: AgingReportWithStatusesByMonth[];
  months: Month[];
  /** The server's version of these reports; lets the next visit skip the download when nothing changed */
  revision?: string | null;
}

type FacilityPayer = string;
// A payer can have thousands of reports, so a few of them overflow the browser's storage quota. The cache is only a
// shortcut: when it does not fit, drop it instead of failing the load that was about to show the reports.
const reportsStorage = createJSONStorage<Record<FacilityPayer, ReportsState>>(() => ({
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      localStorage.removeItem(key);
    }
  },
  removeItem: (key) => localStorage.removeItem(key),
}));
export const reportsPerFacilityPayerState = atomWithStorage(
  "reportsPerFacilityPayerState",
  {} as Record<FacilityPayer, ReportsState>,
  reportsStorage
);
